/**
 * Validators custom pour la présence d'un profil d'utilisation sur le bâtiment
 */

import type { CustomValidator, ValidatorWithContext } from "./registry"
import type { Building } from "@types"

/**
 * Indique si le bâtiment a un profil d'utilisation lié (objet peuplé et identifiant du profil).
 */
function isBuildingProfilUtilisationLinked(building: Building): boolean {
    const pu = building.profil_utilisation
    if (pu == null) {
        return false
    }
    const id = pu._id_profilutilisation
    if (id == null) {
        return false
    }
    if (typeof id === "string") {
        return id.length > 0
    }
    return true
}

/**
 * Validator : le graphique n'est affiché que si le bâtiment a un profil d'utilisation associé.
 *
 * @returns true si `profil_utilisation` est défini et `profil_utilisation._id_profilutilisation` est renseigné
 *
 * @example Configuration API (visible_conditions d'un graphique) — avec message d'erreur :
 * ```json
 * {
 *   "conditions": [
 *     {
 *       "operator": "custom",
 *       "validatorId": "building_profil_utilisation_linked",
 *       "origin": "building"
 *     }
 *   ],
 *   "error": {
 *     "message": "Associez un profil d'utilisation au bâtiment pour afficher ce graphique."
 *   }
 * }
 * ```
 *
 * `origin` doit être `"building"` pour que le contexte bâtiment soit utilisé (voir DataChartListItem / PageBuildingData).
 */
const buildingProfilUtilisationLinkedValidator: CustomValidator = (
    _: unknown,
    context: { building?: Building }
): boolean => {
    const building = context.building
    if (!building) {
        return false
    }
    return isBuildingProfilUtilisationLinked(building)
}

/**
 * Validator building_profil_utilisation_linked (pas de buildContext : contexte minimal `{ building }` suffit)
 */
export const buildingProfilUtilisationLinked: ValidatorWithContext = {
    validator: buildingProfilUtilisationLinkedValidator,
}
